import { Button } from '@/components/atom/Button';
import { TextDetailRow } from '@/components/atom/TextDetailRow';
import { TokenUnit } from '@/components/atom/TokenUnit';
import { useMixpanel } from '@/hooks/mixpanel/mixpanel';
import { useTranslation } from '@/hooks/support/translate';
import {
    formatEther,
    isAllowedNumber,
    isNull,
    isValidNumberKeypress,
    Maybe,
    numberToFixed,
    parseEther,
} from '@/utils/common';
import classNames from 'classnames';
import { BigNumber } from 'ethers';
import {
    ChangeEventHandler,
    FocusEventHandler,
    KeyboardEventHandler,
    MouseEventHandler,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { ensuredForwardRef } from 'react-use';

export type UnitInputRef = {
    clear: () => void;
    setValue: (val: BigNumber) => void;
    focus: () => void;
};
type UnitInputProps = {
    label: string | React.ReactNode;
    secondaryLabel?: string | React.ReactNode;
    symbol?: string;
    secondarySymbol?: Maybe<string>;
    balance?: BigNumber;
    isBalanceLoading?: boolean;
    maxValue?: Maybe<BigNumber>;
    maxValueDeductions?: BigNumber;
    isSecondary?: boolean;
    className?: string;
    labelAction?: string | React.ReactNode;
    onUpdate?: (value: BigNumber) => void;
    onUpdateInputBlur?: (value: BigNumber) => void;
    onError?: (errCode: string) => void;
    isError?: boolean;
    showEmptyBalance?: boolean;
    isReadonly?: boolean;
    value?: BigNumber;
};
const MAX_PRECISION_DIGITS = 5;
const MAX_PRECISION_DIGITS_FOR_SMALL_VALS = 16;
export const UnitInput = ensuredForwardRef<UnitInputRef, UnitInputProps>(
    (
        {
            label,
            secondaryLabel,
            symbol,
            secondarySymbol = null,
            balance,
            isBalanceLoading = false,
            maxValue: _maxValue,
            maxValueDeductions = BigNumber.from(0),
            isSecondary = false,
            className,
            labelAction,
            onUpdate,
            onUpdateInputBlur,
            onError,
            isError = false,
            showEmptyBalance = true,
            isReadonly,
            value,
        },
        ref,
    ) => {
        const { t } = useTranslation();
        const { track } = useMixpanel();
        const inputEl = useRef<HTMLInputElement>(null);

        const [inputValue, setInputValue] = useState('');

        useImperativeHandle(ref, () => ({
            clear: () => {
                !isNull(inputEl.current) && (inputEl.current.value = '');
            },
            setValue: (val: BigNumber) => {
                if (!val || !inputEl.current) {
                    return;
                }
                const unFormattedValue = Number(formatEther(val));
                inputEl.current!.value =
                    unFormattedValue === 0
                        ? '0'
                        : unFormattedValue > 0.0001
                        ? (numberToFixed(val, MAX_PRECISION_DIGITS) as string)
                        : (numberToFixed(val, MAX_PRECISION_DIGITS_FOR_SMALL_VALS) as string);
            },
            focus: () => {
                inputEl.current!.focus();
            },
        }));

        const emitUpdate = (value: BigNumber) => {
            onUpdate!(value);
        };

        const handleMaxClick: MouseEventHandler<HTMLButtonElement> = (e) => {
            track('STAKE-PRIMARY-UNITINPUT-MAX');
            e.stopPropagation();
            const maxValue = _maxValue!.sub(maxValueDeductions!);
            emitUpdate(maxValue);
            inputEl.current!.value = numberToFixed(maxValue, MAX_PRECISION_DIGITS) as string;
        };

        const handleInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
            if (isReadonly) return;
            const value = e.target!.value;
            if (!isAllowedNumber(value)) {
                return;
            }
            const emitValue = value.length === 0 ? BigNumber.from(0) : parseEther(e.target!.value);
            emitUpdate(emitValue);
        };

        const restrictE: KeyboardEventHandler<HTMLInputElement> = (e) => {
            if (!isValidNumberKeypress(e.key) || !isAllowedNumber(inputValue)) {
                e.preventDefault();
            }
        };

        const handleInputBlur: FocusEventHandler<HTMLInputElement> = (e) => {
            const value = e.target!.value;
            setInputValue(value);
            if (!isAllowedNumber(value)) {
                e.preventDefault();
                return;
            }
            const emitValue = value.length === 0 ? BigNumber.from(0) : parseEther(e.target!.value);
            onUpdateInputBlur && onUpdateInputBlur(emitValue);
        };

        return (
            <>
                <div
                    className={classNames(
                        className,
                        'text-neutral-20 lg:p-4 p-2 ring-transparent caret-main-60 input--primary placeholder-gray-600 bg-grey-50 rounded-xl relative focus-within:ring-main-50',
                        {
                            '!bg-transparent focus-within:ring-main-30/50 py-2': isSecondary,
                            'ring-red-60': isError,
                            'pointer-events-none': isReadonly,
                        },
                    )}
                >
                    <div className="flex items-center justify-between mb-2 text-grey-10">
                        {label}
                        <div data-type="label-action">{labelAction}</div>
                    </div>
                    <div className="flex items-end justify-between">
                        <input
                            onKeyPress={restrictE}
                            className="w-full py-2 pl-2 text-white bg-transparent outline-none t__heading--h4 read-only:text-grey-50 placeholder-grey-40"
                            type="number"
                            autoComplete="off"
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                            ref={inputEl}
                            placeholder="0.0"
                            readOnly={false}
                            min={0}
                            value={value && formatEther(value)}
                        />
                        {secondaryLabel && <div className="mb-3">{secondaryLabel}</div>}
                    </div>
                    {balance && balance.gt && (showEmptyBalance || balance.gt(0)) && (
                        <TextDetailRow
                            size="small"
                            caption={t('slug.stakeCard.maxAvailable')}
                            value={
                                <div className="flex items-center space-x-2">
                                    <TokenUnit
                                        value={balance!}
                                        symbol={secondarySymbol || symbol}
                                        isLoading={isBalanceLoading}
                                    />
                                    {balance.gt && balance?.gt(0) && !isReadonly && (
                                        <Button
                                            id="stake-max"
                                            onClick={handleMaxClick}
                                            size="small"
                                            isDisabled={!_maxValue?.gt(0)}
                                            variant="ghost"
                                            name="Max"
                                            className="uppercase"
                                        >
                                            {t('slug.stakeCard.max')}
                                        </Button>
                                    )}
                                </div>
                            }
                        />
                    )}
                </div>
            </>
        );
    },
);

UnitInput.defaultProps = {
    maxValueDeductions: BigNumber.from(0),
    secondarySymbol: null,
    isBalanceLoading: false,
};
